import { useState, useEffect } from 'react';
import { ModuleProps } from '../components/Module';
import {
  modulePlaceholderMap,
  MODULE_STATUS_FAILED_LOADING_MODULE,
  MODULE_STATUS_FAILED_LOADING_SCRIPT,
  MODULE_STATUS_LOADING_SCRIPT,
  MODULE_STATUS_NOT_SPECIFIED
} from '../components/ModulePlaceholders';
import { getModuleFactory } from '../factories/getModuleFactory';
import { moduleConfigFactory } from '../factories/moduleConfigFactory';
import { useDynamicScript } from './useDynamicScript';

/**
 * Hook to load a module and return its content as an object
 * @see useModuleManifest
 * @param name the name of the module in the manifest
 * @returns the module config
 */
export const useModuleObject = ({
  appName,
  moduleName,
  loadingElement,
  module,
  log = () => {},
  error = () => {}
}: { appName?: string; moduleName?: string } & Partial<Omit<ModuleProps, 'moduleProps'>>) => {
  const theModule = module ?? moduleConfigFactory(appName!, moduleName!);
  const { ready, failed } = useDynamicScript({ url: theModule?.url, log, error });

  const modulePlaceholder = {
    moduleConstructor: (): any => modulePlaceholderMap[MODULE_STATUS_NOT_SPECIFIED](),
    ready: false,
    failed: false
  };
  const [moduleObject, setModuleObject] = useState<Record<any, any>>(modulePlaceholder);
  const { factory, isLazy } = getModuleFactory(theModule.appName, theModule.module, log);

  useEffect(() => {
    if (!theModule?.url || !theModule?.appName || !theModule?.module) {
      setModuleObject(Object.assign(modulePlaceholder, { ready, failed }));
      return;
    }

    if (!ready && !failed) {
      modulePlaceholder.moduleConstructor = () => loadingElement ?? modulePlaceholderMap[MODULE_STATUS_LOADING_SCRIPT](theModule.url);
      setModuleObject(Object.assign(modulePlaceholder, { ready, failed }));
      return;
    }

    if (failed) {
      modulePlaceholder.moduleConstructor = () => modulePlaceholderMap[MODULE_STATUS_FAILED_LOADING_SCRIPT](theModule.url);
      modulePlaceholder.failed = true;
      setModuleObject(Object.assign(modulePlaceholder, { ready, failed }));
      return;
    }

    if (isLazy) {
      const getFromFactory = async (factoryMethod: Function) => {
        try {
          const m = await factoryMethod();
          setModuleObject(Object.assign(m, { ready, failed }));
        } catch {
          modulePlaceholder.moduleConstructor = (): any => modulePlaceholderMap[MODULE_STATUS_FAILED_LOADING_MODULE](theModule.url);
          setModuleObject(Object.assign(modulePlaceholder, { ready: false, failed: true }));
        }
      };
      getFromFactory(factory);
      return;
    }
    setModuleObject(Object.assign(factory.default, { ready, failed }));
  }, [isLazy, factory, ready, failed, theModule]);

  return moduleObject;
};
